import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCashbookStatus } from "../services/cashBookApi";
import type { CashBadge, CashRole, CashStatus } from "../pages/karkhana/cashbook/types";

/**
 * Is the paid Cash book add-on (CASHBOOK) on for this studio? The server decides (V1_SPEC D2):
 * `GET /karkhana/cash-book/status` says `enabled` = kill switch on AND studio entitled. A server answer
 * that says off (enabled false, 403/404, an old backend without the endpoint) means off, so studios
 * without it keep today's screens. No network or a 5xx is not an answer: the last known value stays
 * (an offline phone keeps its नकद tab and photo queue). One answer is kept in memory for 60 s and
 * shared by every caller on the page, and the last answer is kept on the phone per studio and user, so
 * an app opened offline starts from it.
 */
export const CACHE_MS = 60_000;

const ZERO: CashBadge = { missing: 0, incomingTransfers: 0, rejectedTransfers: 0, toCheck: 0 };
export const OFF: CashStatus = { enabled: false, role: null, badge: ZERO };

let cached: { value: CashStatus; at: number; scope: string | null } | null = null;
let inFlight: Promise<CashStatus> | null = null;
const listeners = new Set<(s: CashStatus) => void>();

const n = (x: unknown) => (typeof x === "number" && isFinite(x) && x > 0 ? Math.floor(x) : 0);

/** The server answer, read defensively: only `enabled === true` turns it on. */
export const parseStatus = (res: any): CashStatus => {
  const d = res?.success ? res.data : null;
  if (!d || d.enabled !== true) return OFF;
  const role: CashRole | null = d.role === "SUPERVISOR" || d.role === "MANAGER" || d.role === "OWNER" ? d.role : null;
  const b = d.badge || {};
  return {
    enabled: true,
    role,
    badge: { missing: n(b.missing), incomingTransfers: n(b.incomingTransfers), rejectedTransfers: n(b.rejectedTransfers), toCheck: n(b.toCheck) },
  };
};

/** Whose answer this is: studio + user (a shared phone never shows one person's answer to the next). */
export const statusScope = (): string | null => {
  try {
    const u = JSON.parse(localStorage.getItem("authUser") || "null")?.users;
    if (!u) return null;
    return `${u.organizationResponse?._id || "org"}.${u._id || u.id || u.userId || u.email || u.mobile || "me"}`;
  } catch {
    return null;
  }
};

const KEY = "hkp.cashbook.status.";

const remember = (scope: string | null, value: CashStatus) => {
  if (!scope) return;
  try {
    localStorage.setItem(KEY + scope, JSON.stringify(value));
  } catch {
    // Storage blocked: the memory copy still works for this visit.
  }
};

const recalled = (scope: string | null): CashStatus | null => {
  if (!scope) return null;
  try {
    const raw = localStorage.getItem(KEY + scope);
    return raw ? parseStatus({ success: true, data: JSON.parse(raw) }) : null;
  } catch {
    return null;
  }
};

/** The answer for the signed-in person: memory, else the copy on the phone (stale, so it is asked again). */
const current = () => {
  const scope = statusScope();
  if (cached && cached.scope === scope) return cached;
  const kept = recalled(scope);
  cached = kept ? { value: kept, at: 0, scope } : null;
  return cached;
};

const publish = (value: CashStatus, at: number, scope: string | null) => {
  cached = { value, at, scope };
  listeners.forEach((l) => l(value));
  return value;
};

/**
 * What a reply means: an answer (on, or off from the server) or nothing (network down, 5xx, a body
 * that is not the server's), in which case the last known value is kept.
 */
export const readReply = (res: any): CashStatus | null => {
  if (res?.success) return parseStatus(res);
  const code = Number(res?.status);
  return code >= 400 && code < 500 ? OFF : null;
};

/** Fetch unless a fresh answer is cached (`force` skips the cache). Never rejects. */
export const loadCashbookStatus = (force = false): Promise<CashStatus> => {
  const now = current();
  if (!force && now && Date.now() - now.at < CACHE_MS) return Promise.resolve(now.value);
  if (inFlight) return inFlight;
  const scope = statusScope();
  inFlight = Promise.resolve()
    .then(() => getCashbookStatus())
    .then(readReply, () => null)
    .then((answer) => {
      if (answer) {
        remember(scope, answer);
        return publish(answer, Date.now(), scope);
      }
      // No answer: keep what we knew (asked again on the next page), never turn a known ON off.
      const known = cached && cached.scope === scope ? cached.value : recalled(scope);
      return publish(known || OFF, 0, scope);
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

/** Sync read of the last answer (false until one arrives). */
export const isCashbookOn = (): boolean => !!current()?.value.enabled;

/** The last answer, or off. */
export const cachedCashbookStatus = (): CashStatus => current()?.value || OFF;

/** Tests and logout: forget the answer in memory. */
export const resetCashbookStatus = () => {
  cached = null;
  inFlight = null;
};

/**
 * `{ enabled, role, badge, refresh }`; asks again on each page change (from the 60 s cache when fresh).
 * `active=false` waits (the Karkhana frame passes false while the login is being renewed).
 */
export const useCashbookStatus = (active = true): CashStatus & { loaded: boolean; refresh: () => Promise<CashStatus> } => {
  const { pathname } = useLocation();
  const [state, setState] = useState<{ status: CashStatus; loaded: boolean }>(() => ({ status: cachedCashbookStatus(), loaded: !!current() }));
  useEffect(() => {
    const on = (s: CashStatus) => setState({ status: s, loaded: true });
    listeners.add(on);
    return () => {
      listeners.delete(on);
    };
  }, []);
  useEffect(() => {
    if (!active) return;
    let live = true;
    loadCashbookStatus().then((s) => live && setState({ status: s, loaded: true }));
    return () => {
      live = false;
    };
  }, [active, pathname]);
  const refresh = useCallback(() => loadCashbookStatus(true), []);
  return { ...state.status, loaded: state.loaded, refresh };
};
